import '@skyscanner-internal/backpack-web/bpk-stylesheets/base';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/base.css';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/font';

import { useEffect, useMemo, useRef, type ReactNode } from 'react';

import { BpkProvider } from '@skyscanner-internal/backpack-web/bpk-component-layout';
import { getDefaultNewRelicConfig, getRumSetupHtml } from '@skyscanner-internal/mshell-node-rum';
import {
  COMPONENTLOAD_FAILURE,
  CONSENT_ID_SERVICE_URL,
  ENABLE_CONSENT_ID_SERVICE,
  ENABLE_COOKIE_VERSION_ROLLBACK,
  ENABLE_KETCH_CMP,
  ENABLE_KETCH_PSID_SYNC,
  ENABLE_LEGACY_CONSENT_SYNC,
  ENABLE_PURPOSE_SCOPED_ID_EXISTING_GDPR_USERS,
  ENABLE_PURPOSE_SCOPED_ID_NON_REGULATED_MARKETS,
  ENABLE_PURPOSE_SCOPED_ID_SERVICE,
  KETCH_CONSENT_COOKIE_NAME,
  KETCH_SCRIPT_URL,
  KETCH_TCF_STORAGE_KEY,
  PURPOSE_SCOPED_ID_SERVICE_KETCH_CMP_URL,
  PURPOSE_SCOPED_ID_SERVICE_LEGACY_CMP_URL,
  WEB_DISTRIBUTED_TRACING_GENERAL_ENABLED,
  WEB_DISTRIBUTED_TRACING_TRAVEL_TOOLS_ENABLED,
} from '@web-platform/shared-travel-tools-constants';
import { getGcsProps } from '@web-platform/shared-travel-tools-data-access-backend/src/global-components-props';
import { getLocalisationData } from '@web-platform/shared-travel-tools-data-access-backend/src/localisation';
import { getPageContext } from '@web-platform/shared-travel-tools-data-access-backend/src/page-context';
import { PageContextProvider } from '@web-platform/shared-travel-tools-data-access-frontend';
import { getTranslations } from '@web-platform/shared-travel-tools-translations';
import {
  config as serverConfig,
  getOpenGraphTags,
} from '@web-platform/shared-travel-tools-utils-backend';
import {
  DeviceProvider,
  getHoustonUrl,
  I18nContext,
  isBrowser,
  logger,
} from '@web-platform/shared-travel-tools-utils-frontend';
import { I18nService, getPageName } from '@web-platform/shared-travel-tools-utils-universal';
import {
  data,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useRouteLoaderData,
  type LoaderFunctionArgs,
} from 'react-router';
import { requestContextParameters } from 'request-context-skyscanner';
import { GLOBAL } from 'saddlebag-browser';
import { initTrackingPreferences } from 'saddlebag-tracking-preferences';
import { getTrackingSetupAttributes } from 'trackolding';

import { GlobalNotFound } from './components/GlobalNotFound/GlobalNotFound';
import { PageSkeleton } from './components/PageSkeleton/PageSkeleton';
import { useIsNotFound } from './hooks/useIsNotFound';

import type { ExtractLoaderData } from './types';

export const loader = async ({
  context: { config, context, logger: requestLogger, perimeterXScriptAttrs, polyfillScriptAttrs },
}: LoaderFunctionArgs) => {
  const culture = context[requestContextParameters.CULTURE];
  const trackingContext = context[requestContextParameters.TRACKING_CONTEXT];
  const domain = context[requestContextParameters.FULL_DOMAIN_NAME];
  const pageType = context[requestContextParameters.PAGE_TYPE];
  const isRightToLeft = context[requestContextParameters.IS_RIGHT_TO_LEFT];
  const { osName } = context[requestContextParameters.DEVICE];
  const { market } = culture;

  const ogTags = domain ? getOpenGraphTags(domain) : null;

  const defaultNewRelicConfig = getDefaultNewRelicConfig();
  defaultNewRelicConfig.distributed_tracing = {
    enabled:
      config.getBooleanValue(WEB_DISTRIBUTED_TRACING_GENERAL_ENABLED) &&
      config.getBooleanValue(WEB_DISTRIBUTED_TRACING_TRAVEL_TOOLS_ENABLED),
  };

  const rumSetupHtml = getRumSetupHtml(
    {},
    pageType,
    { pageType, market },
    { includeScriptTag: false, agent: { init: defaultNewRelicConfig } },
  );

  const resolvedTrackingContext = {
    ...trackingContext,
    pageContext: {
      pageType,
      analyticsPageName: getPageName(pageType),
      uaPageView: '/',
      pageName: getPageName(pageType),
    },
    microsite: { name: 'travel-tools' },
    config: {
      ...trackingContext.config,
      // The topic prefix for analytics events (regardless of NODE_ENV)
      env: 'public',
      behavioural: { enabled: true },
    },
  };

  const trackoldingSetupAttrs = getTrackingSetupAttributes(resolvedTrackingContext);
  const isProductionEnv = serverConfig.env === 'production';

  const [gcs, cldr] = await Promise.all([
    getGcsProps({
      config,
      context,
      culture,
      domain,
      isProductionEnv,
      logger: requestLogger,
      osName,
      pageType,
    }),
    getLocalisationData(culture),
  ]);

  const pageContext = getPageContext({ config, context });
  const {
    isBrowser: isBrowserDevice,
    isMobilePhone,
    isTablet,
  } = context[requestContextParameters.DEVICE];
  const canonicalLink = context[requestContextParameters.PAGE_CONTEXT_DATA]?.canonical_url;

  const trackingPrefsConfigs = {
    enableKetchCMP: config.getBooleanValue(ENABLE_KETCH_CMP),
    ketchScriptUrl: config.getStringValue(KETCH_SCRIPT_URL),
    ketchConsentCookieName: config.getStringValue(KETCH_CONSENT_COOKIE_NAME),
    ketchTCFStorageKey: config.getStringValue(KETCH_TCF_STORAGE_KEY),
    enableCookieVersionRollback: config.getBooleanValue(ENABLE_COOKIE_VERSION_ROLLBACK),
    enablePurposeScopedIdService: config.getBooleanValue(ENABLE_PURPOSE_SCOPED_ID_SERVICE),
    enablePurposeScopedIdExistingGDPRUsers: config.getBooleanValue(
      ENABLE_PURPOSE_SCOPED_ID_EXISTING_GDPR_USERS,
    ),
    enablePurposeScopedIdNonRegulatedMarkets: config.getBooleanValue(
      ENABLE_PURPOSE_SCOPED_ID_NON_REGULATED_MARKETS,
    ),
    purposeScopedIdServiceLegacyCmpUrl: config.getStringValue(
      PURPOSE_SCOPED_ID_SERVICE_LEGACY_CMP_URL,
    ),
    enableConsentIdService: config.getBooleanValue(ENABLE_CONSENT_ID_SERVICE),
    enableKetchPsidSync: config.getBooleanValue(ENABLE_KETCH_PSID_SYNC),
    purposeScopedIdServiceKetchCmpUrl: config.getStringValue(
      PURPOSE_SCOPED_ID_SERVICE_KETCH_CMP_URL,
    ),
    enableLegacyConsentSync: config.getBooleanValue(ENABLE_LEGACY_CONSENT_SYNC),
    consentIdServiceUrl: config.getStringValue(CONSENT_ID_SERVICE_URL),
  };

  return data({
    dir: isRightToLeft ? 'rtl' : 'ltr',
    ogTags,
    rumSetupHtml,
    polyfillScriptAttrs,
    perimeterXScriptAttrs,
    canonicalLink,
    trackoldingSetupAttrs,
    gcs,
    i18n: { cldr, culture, translations: getTranslations(culture.locale) },
    isProductionEnv,
    pageContext,
    device: { isBrowser: isBrowserDevice, isMobilePhone, isTablet },
    trackingPrefsConfigs,
  });
};

export function Layout({ children }: { children: ReactNode }) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const rootData = useRouteLoaderData<ExtractLoaderData<typeof loader>>('root')!;

  return (
    <html lang={rootData.pageContext.locale} dir={rootData.dir}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta name="y_key" content="3ccd97fca3bc5b05" />
        {rootData.ogTags &&
          Object.entries(rootData.ogTags).map(([property, content]) => (
            <meta key={property} property={property} content={String(content)} />
          ))}
        <Meta />
        <link rel="manifest" href="/android-chrome-manifest.json" />
        <link rel="preconnect" href="https://js.skyscnr.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://content.skyscnr.com" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="//www.googletagmanager.com" />
        <link rel="dns-prefetch" href="//bam.nr-data.net" />
        {rootData.canonicalLink && <link rel="canonical" href={rootData.canonicalLink} />}
        <Links />
        {rootData.polyfillScriptAttrs && <script {...rootData.polyfillScriptAttrs} />}
        <script {...rootData.perimeterXScriptAttrs} />
        {rootData.rumSetupHtml && (
          // eslint-disable-next-line react/no-danger
          <script dangerouslySetInnerHTML={{ __html: rootData.rumSetupHtml }} />
        )}
      </head>
      <body>
        {/* #pagewrap used for accessibility by Backpack components, do not change */}
        <div id="pagewrap">{children}</div>
        <ScrollRestoration />
        <Scripts />
        {rootData.trackoldingSetupAttrs && <script {...rootData.trackoldingSetupAttrs} />}
      </body>
    </html>
  );
}

export default function App() {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const rootData = useRouteLoaderData<ExtractLoaderData<typeof loader>>('root')!;
  const isNotFound = useIsNotFound();
  const i18nService = useMemo(
    () => I18nService(rootData.i18n, logger),
    // Service is intentionally created once per page load — never re-run within a single load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Guard with useRef so this runs at most once per client mount.
  // StrictMode may double-invoke in development, but production is unaffected.
  // This mirrors hotels-website's pattern.
  const trackingInitialised = useRef(false);
  if (isBrowser() && !trackingInitialised.current) {
    trackingInitialised.current = true;
    initTrackingPreferences({
      ...rootData.trackingPrefsConfigs,
      consumer: 'travel-tools',
      market: rootData.i18n.culture.market,
    });
  }

  return (
    <PageContextProvider value={rootData.pageContext}>
      <DeviceProvider value={rootData.device}>
        <I18nContext.Provider value={i18nService}>
          <BpkProvider>
            <PageSkeleton
              gcs={rootData.gcs}
              isProductionEnv={rootData.isProductionEnv}
              showHeader={!isNotFound}
              showFooterFlags={!isNotFound}
              pageContext={rootData.pageContext}
            >
              <Outlet />
            </PageSkeleton>
          </BpkProvider>
        </I18nContext.Provider>
      </DeviceProvider>
    </PageContextProvider>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  const loaderData = useRouteLoaderData<ExtractLoaderData<typeof loader>>('root');
  const isProductionEnv = loaderData?.isProductionEnv ?? false;
  const is4xx = isRouteErrorResponse(error) && [400, 403, 404].includes(error.status);

  useEffect(() => {
    if (is4xx) return;

    logger.event(COMPONENTLOAD_FAILURE);
    logger.error('Caught error in ErrorBoundary', {}, error);
    const houstonUrl = getHoustonUrl('travel-tools', isProductionEnv);
    const win = GLOBAL.getWindow();
    if (houstonUrl && win) {
      win.location.replace(houstonUrl);
    }
  }, [is4xx, isProductionEnv, error]);

  if (is4xx) {
    return <GlobalNotFound />;
  }

  // At this point, we know we're in a non-production environment.
  //
  // In production:
  // - Server-side errors trigger a redirect to Houston via getHoustonUrl().
  // - Client-side errors are handled via window.location.replace() in useEffect.
  //
  // Therefore, the only case where this fallback UI is ever shown is in development/e2e/staging environments.
  // We can safely render the raw error for debugging purposes here.

  // Important:
  // - Do NOT log server-side errors here.
  // - Instead, any server-side error logging must be handled in the entry.server.tsx or the loader/action functions for special cases.
  return (
    <pre>
      {(isRouteErrorResponse(error) &&
        `Route error ${error.status} ${error.statusText}\n${String(error.data ?? '')}`) ||
        (error instanceof Error && `${error.message}\n${error.stack ?? ''}`) ||
        String(error)}
    </pre>
  );
}
